import { view, prop } from '@vscode/observables-react';
import { styles } from './styles.js';
import { TitleBarButton } from './TitleBarButton.js';
import { PanelLeftIcon, PanelRightIcon, SunIcon, MoonIcon } from './icons.js';
import type { ExplorerModel, ViewMode } from './ExplorerModel.js';

const ViewModeSelector = view(
  { model: prop.const<ExplorerModel>() },
  (reader, props) => {
    const model = props.model;
    const viewMode = model.viewMode.read(reader);

    const sessions = model.daemon?.sessions.read(reader) ?? [];
    const currentSession = model.daemon?.sessionName;
    const otherSessions = sessions.filter(s => s.name !== currentSession);

    if (sessions.length <= 1) return null;

    return (
      <div style={viewModeStyles.container}>
        <div style={viewModeStyles.segmentedControl}>
          <ViewModeButton label="Live" mode="session" active={viewMode === 'session'} onClick={() => model.setViewMode('session')} />
          <ViewModeButton label="Diff" mode="diff" active={viewMode === 'diff'} onClick={() => model.setViewMode('diff')} />
        </div>
        {otherSessions.length > 0 && (
          <div style={viewModeStyles.sessionLinks}>
            {otherSessions.map(s => (
              <button
                key={s.name}
                style={viewModeStyles.sessionLink}
                onClick={() => model.navigateToSession(s.name)}
                title={`Switch to ${s.name} session`}
              >
                → {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

function ViewModeButton({ label, mode, active, onClick }: {
  label: string; mode: ViewMode; active: boolean; onClick: () => void;
}) {
  return (
    <button
      style={active ? viewModeStyles.segmentActive : viewModeStyles.segment}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export const TitleBar = view(
  {
    model: prop.const<ExplorerModel>(),
  },
  (reader, props) => {
    const model = props.model;
    const leftVisible = model.leftSidebarVisible.read(reader);
    const rightVisible = model.rightSidebarVisible.read(reader);
    const isDarkTheme = model.isDarkTheme.read(reader);
    const selectedNode = model.selectedNode.read(reader);

    return (
      <div style={styles.titleBar}>
        <div style={styles.titleBarSection}>
          <TitleBarButton
            active={leftVisible}
            onClick={model.toggleLeftSidebar}
            title="Toggle left sidebar"
          >
            <PanelLeftIcon />
          </TitleBarButton>
        </div>
        <div style={styles.titleBarCenter}>
          {model.isReportMode ? (
            <span style={{ fontSize: 12, color: 'var(--component-explorer-descriptionForeground)' }}>
              Screenshot Report
            </span>
          ) : model.daemonAvailable ? (
            <ViewModeSelector model={model} />
          ) : null}
          {selectedNode ? (
            <>
              <span style={{ color: 'var(--component-explorer-textLink-foreground)' }}>&lt;</span>
              <span>{selectedNode.name}</span>
              <span style={{ color: 'var(--component-explorer-textLink-foreground)' }}>/&gt;</span>
            </>
          ) : (
            <span style={{ color: 'var(--component-explorer-descriptionForeground)' }}>No component selected</span>
          )}
        </div>
        <div style={styles.titleBarSection}>
          <TitleBarButton
            active={false}
            onClick={model.toggleTheme}
            title={isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {isDarkTheme ? <SunIcon /> : <MoonIcon />}
          </TitleBarButton>
          <TitleBarButton
            active={rightVisible}
            onClick={model.toggleRightSidebar}
            title="Toggle right sidebar"
          >
            <PanelRightIcon />
          </TitleBarButton>
        </div>
      </div>
    );
  }
);

const viewModeStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginRight: 12,
  } as React.CSSProperties,
  segmentedControl: {
    display: 'flex',
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid var(--component-explorer-panel-border)',
  } as React.CSSProperties,
  segment: {
    padding: '2px 10px',
    fontSize: 11,
    fontWeight: 500,
    border: 'none',
    background: 'transparent',
    color: 'var(--component-explorer-descriptionForeground)',
    cursor: 'pointer',
  } as React.CSSProperties,
  segmentActive: {
    padding: '2px 10px',
    fontSize: 11,
    fontWeight: 500,
    border: 'none',
    background: 'var(--component-explorer-button-background)',
    color: 'var(--component-explorer-button-foreground)',
    cursor: 'pointer',
  } as React.CSSProperties,
  sessionLinks: {
    display: 'flex',
    gap: 4,
  } as React.CSSProperties,
  sessionLink: {
    padding: '2px 8px',
    fontSize: 11,
    border: '1px solid var(--component-explorer-panel-border)',
    borderRadius: 4,
    background: 'transparent',
    color: 'var(--component-explorer-textLink-foreground)',
    cursor: 'pointer',
  } as React.CSSProperties,
};
